import { Box, Dialog, Divider, Grid, IconButton, Step, StepLabel, Stepper, Typography, useTheme } from "@mui/material";
import { DialogCloseIcon } from "../../assets/SvgIcons";
import { AuditDetailsSlabObj } from "../../redux-store/types/onBoarding";
import dayjs from "../../utils/dayjs";
import { toINR } from "../../utils/utilityFunctions";
import { useSelector } from "react-redux";
import { RootStateType } from "../../redux-store/reducers";

function FixedFeeTypeCard({
    label,
    feeTypeValue
}: {
    label: string;
    feeTypeValue: string;
}): JSX.Element {
    const theme = useTheme();

    // Data from Redux
    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);

    const cardBaseStyles = {
        backgroundColor: theme.palette.customColors.darkGrey,
        borderRadius: "12px",
        padding: "16px 20px",
        flexGrow: 1,
    };

    return (
        <Box sx={cardBaseStyles}>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    columnGap: "48px",
                    rowGap: "8px",
                    marginBottom: "8px",
                }}
            >
                <Typography variant="subtitle_500" sx={{ color: theme.palette.grey[500] }}>
                    {label} Fee Type
                </Typography>
                <Typography variant="subtitle_500" sx={{ color: theme.palette.grey[500] }}>
                    Annual Fixed Fee Amount (₹)
                </Typography>
            </Box>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    columnGap: "48px",
                    rowGap: "8px",
                }}
            >
                <Typography variant="subtitle_500" sx={{ color: theme.palette.text.primary }}>
                    Fixed Fee
                </Typography>
                <Typography
                    variant="subtitle_500"
                    sx={{
                        color: theme.palette.text.primary,
                        whiteSpace: "break-spaces",
                        wordBreak: "break-all"
                    }}
                >
                    {(feeTypeValue !== "") ? toINR({ value: feeTypeValue ?? "", locale, currencyCode }) : "-"}
                </Typography>
            </Box>
        </Box>
    );
}

function VariableFeeTypeCard({
    label,
    feeTypeValue
}: {
    label: string;
    feeTypeValue: string;
}): JSX.Element {
    const theme = useTheme();

    const cardBaseStyles = {
        backgroundColor: theme.palette.customColors.darkGrey,
        borderRadius: "12px",
        padding: "16px 20px",
        flexGrow: 1,
    };

    return (
        <Box sx={cardBaseStyles}>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    columnGap: "48px",
                    rowGap: "8px",
                    marginBottom: "8px",
                }}
            >
                <Typography variant="subtitle_500" sx={{ color: theme.palette.grey[500] }}>
                    {label} Fee Type
                </Typography>
                <Typography variant="subtitle_500" sx={{ color: theme.palette.grey[500] }}>
                    Fee Percentage P.A (%)
                </Typography>
            </Box>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    columnGap: "48px",
                    rowGap: "8px",
                }}
            >
                <Typography variant="subtitle_500" sx={{ color: theme.palette.text.primary }}>
                    AUA Fee (Variable)
                </Typography>
                <Typography
                    variant="subtitle_500"
                    sx={{
                        color: theme.palette.text.primary,
                        whiteSpace: "break-spaces",
                        wordBreak: "break-all"
                    }}
                >
                    {(feeTypeValue !== "") ? feeTypeValue : "-"}
                </Typography>
            </Box>
        </Box>
    );
}

function SlabFeeTypeCard({
    label,
    slabRows
}: {
    label: string;
    slabRows: Array<AuditDetailsSlabObj>;
}): JSX.Element {
    const theme = useTheme();

    // Data from Redux
    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);

    const cardBaseStyles = {
        backgroundColor: theme.palette.customColors.darkGrey,
        borderRadius: "12px",
        padding: "16px 20px",
        flexGrow: 1,
    };

    const scrollbarStyles = {
        "&::-webkit-scrollbar": { width: "6px" },
        "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
            borderRadius: "3px",
        },
        "&::-webkit-scrollbar-thumb": {
            backgroundColor: theme.palette.primary[500],
            borderRadius: "3px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: theme.palette.primary[500],
        },
    };

    return (
        <Box
            sx={{
                ...cardBaseStyles,
                maxHeight: "300px",
                overflowY: "auto",
                ...scrollbarStyles,
            }}
        >
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr 1fr",
                    gap: "8px",
                    marginBottom: "16px",
                }}
            >
                <Typography variant="subtitle_500" sx={{ color: theme.palette.grey[500] }}>
                    {label} Fee Type
                </Typography>
                <Typography variant="subtitle_500" sx={{ color: theme.palette.grey[500] }}>
                    Fee Percentage P.A (%)
                </Typography>
                <Typography variant="subtitle_500" sx={{ color: theme.palette.grey[500] }}>
                    Min AUM (₹)
                </Typography>
                <Typography variant="subtitle_500" sx={{ color: theme.palette.grey[500] }}>
                    Max AUM (₹)
                </Typography>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", rowGap: "16px" }}>
                {(slabRows).map((row: AuditDetailsSlabObj, index: number) => (
                    <Box
                        key={index}
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr 1fr 1fr",
                            gap: "8px",
                        }}
                    >
                        <Typography variant="subtitle_500" sx={{ color: theme.palette.text.primary }}>
                            {index === 0 ? "AUA Fee (Slab Wise)" : ""}
                        </Typography>
                        <Typography
                            variant="subtitle_500"
                            sx={{
                                color: theme.palette.text.primary,
                                whiteSpace: "break-spaces",
                                wordBreak: "break-all"
                            }}
                        >
                            {row?.rate ?? "-"}
                        </Typography>
                        <Typography
                            variant="subtitle_500"
                            sx={{
                                color: theme.palette.text.primary,
                                whiteSpace: "break-spaces",
                                wordBreak: "break-all"
                            }}
                        >
                            {(row?.slab_min !== null && row?.slab_min !== undefined) ? toINR({ value: row.slab_min ?? "", locale, currencyCode }) : "-"}
                        </Typography>
                        <Typography
                            variant="subtitle_500"
                            sx={{
                                color: theme.palette.text.primary,
                                whiteSpace: "break-spaces",
                                wordBreak: "break-all"
                            }}
                        >
                            {(row?.slab_max !== null && row?.slab_max !== undefined) ? toINR({ value: row.slab_max ?? "", locale, currencyCode }) : "-"}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

function SecurityInfoCard({
    label,
    isExcluded,
    modifiedAt,
    modifiedBy,
    exclusionReason
}: {
    label: string;
    isExcluded: string;
    modifiedAt: string;
    modifiedBy: string;
    exclusionReason: string;
}): JSX.Element {
    const theme = useTheme();

    const cardBaseStyles = {
        backgroundColor: theme.palette.customColors.darkGrey,
        borderRadius: "12px",
        padding: "16px 20px",
        flexGrow: 1,
    };

    return (
        <Box sx={cardBaseStyles}>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr 1fr",
                    gap: "8px",
                    marginBottom: "8px",
                }}
            >
                <Typography variant="subtitle_500" sx={{ color: theme.palette.grey[500] }}>
                    {label} Exclusion Status
                </Typography>
                <Typography variant="subtitle_500" sx={{ color: theme.palette.grey[500] }}>
                    {label} Modified At
                </Typography>
                <Typography variant="subtitle_500" sx={{ color: theme.palette.grey[500] }}>
                    {label} Modified By
                </Typography>
                <Typography variant="subtitle_500" sx={{ color: theme.palette.grey[500] }}>
                    {label} Exclusion Reason
                </Typography>
            </Box>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr 1fr",
                    gap: "8px",
                }}
            >
                <Typography variant="subtitle_500" sx={{ color: theme.palette.text.primary }}>
                    {isExcluded === "Y" ? "Yes" : "No"}
                </Typography>
                <Typography
                    variant="subtitle_500"
                    sx={{
                        color: theme.palette.text.primary,
                        whiteSpace: "break-spaces",
                        wordBreak: "break-all"
                    }}
                >
                    {modifiedAt ? dayjs(modifiedAt).format("DD/MM/YYYY") : "-"}
                </Typography>
                <Typography
                    variant="subtitle_500"
                    sx={{
                        color: theme.palette.text.primary,
                        whiteSpace: "break-spaces",
                        wordBreak: "break-all"
                    }}
                >
                    {modifiedBy || "-"}
                </Typography>
                <Typography
                    variant="subtitle_500"
                    sx={{
                        color: theme.palette.text.primary,
                        whiteSpace: "break-spaces",
                        wordBreak: "break-all"
                    }}
                >
                    {isExcluded === "N" ? "-" : (exclusionReason || "-")}
                </Typography>
            </Box>
        </Box>
    );
}

interface AuditDetailsPopupProps {
    open: boolean;
    onClose: () => void;
    data: any;
    comingFrom: "auditHistory" | "feeSetupHistory"
}

export default function AuditDetailsPopup({
    open,
    onClose,
    data,
    comingFrom
}: AuditDetailsPopupProps): JSX.Element {
    const theme = useTheme();

    const dotStyles = {
        width: "12px",
        height: "12px",
        borderRadius: "50%",
        backgroundColor: theme.palette.primary[500],
        position: "absolute",
        top: "50%",
        left: 0,
        transform: "translateY(-50%)",
        zIndex: 2,
    } as const;

    const lineBase = {
        content: '""',
        position: "absolute",
        left: "4.5px",
        width: "3px",
        backgroundColor: theme.palette.primary[500],
    } as const;

    const MODIFIED_DATE_MAP = {
        auditHistory: data?.newState?.modifiedAt ? dayjs(data.newState.modifiedAt).format('DD/MM/YYYY') : "-",
        feeSetupHistory: data?.modifiedDate ? dayjs(data.modifiedDate).format('DD/MM/YYYY') : "-"
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            sx={{
                "& .MuiPaper-root": {
                    borderRadius: "20px",
                    maxWidth: "600px",
                }
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: { xs: "16px", sm: "20px", md: "24px" }
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", columnGap: "12px" }}>
                    <Typography variant="h2" sx={{ color: theme.palette.text.primary }}>
                        Audit Details
                    </Typography>
                    <Typography variant="body_400" sx={{ color: theme.palette.grey[500] }}>
                        {MODIFIED_DATE_MAP[comingFrom]}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ padding: "4px" }}>
                    <DialogCloseIcon />
                </IconButton>
            </Box>
            <Divider sx={{ borderColor: theme.palette.grey[500] }} />
            <Box
                sx={{
                    padding: { xs: "16px", sm: "20px", md: "24px" },
                    maxHeight: "60vh",
                }}
            >
                <Grid
                    container
                    rowSpacing={3}
                    columnSpacing={6}
                    sx={{ paddingLeft: "28px" }}
                >
                    {[
                        { label: "Individual Name", value: `${data?.investorName ?? "-"}${data?.clientCode ? "\n(" + (data?.clientCode) + ")" : ""}` },
                        { label: "Family Name", value: data?.familyName ?? "-" },
                        { label: "Family ID", value: data?.familyId ?? "-" },
                        ...(["feeSetupHistory"].includes(comingFrom) ? [
                            { label: "Modified By", value: `${data?.modifiedBy ?? "-"}${data?.modifiedById ? "\n(" + (data?.modifiedById) + ")" : ""}` },
                            { label: "Modified Date", value: data?.modifiedDate ? dayjs(data.modifiedDate).format('DD/MM/YYYY') : "-" },
                        ] : []),
                    ].map((item) => (
                        <Grid item xs={4} key={item.label}>
                            <Typography
                                variant="body_400"
                                sx={{ color: theme.palette.grey[500] }}
                            >
                                {item.label}
                            </Typography>
                            <Typography
                                variant="body_400"
                                sx={{
                                    fontWeight: 500,
                                    color: theme.palette.text.primary,
                                    marginTop: "4px",
                                    whiteSpace: "break-spaces",
                                    wordBreak: "break-all"
                                }}
                            >
                                {item.value}
                            </Typography>
                        </Grid>
                    ))}
                </Grid>
                {(
                    ["auditHistory"].includes(comingFrom) &&
                    data?.securityName
                ) ? (
                    <Typography
                        variant="body_500"
                        sx={{
                            color: theme.palette.text.primary,
                            whiteSpace: "break-spaces",
                            wordBreak: "break-all",
                            paddingLeft: "28px",
                            marginTop: "30px",
                            marginBottom: "15px"
                        }}
                    >
                        {data?.securityName ?? ""}
                    </Typography>
                ) : null}
                <Stepper
                    orientation="vertical"
                    activeStep={-1}
                    connector={<></>}
                    sx={{
                        marginTop: "24px",
                        "& .MuiStepLabel-root": { padding: 0 },
                        "& .MuiStepLabel-iconContainer": { display: "none" },
                        "& .MuiStepLabel-labelContainer": { width: "100%", margin: 0 },
                        gap: "24px",
                    }}
                >
                    <Step active>
                        <StepLabel>
                            <Box sx={{ display: "flex", alignItems: "stretch" }}>
                                <Box
                                    sx={{
                                        width: "12px",
                                        flexShrink: 0,
                                        position: "relative",
                                        marginRight: "16px",
                                        "&::after": {
                                            ...lineBase,
                                            top: "50%",
                                            bottom: "-24px",
                                        },
                                    }}
                                >
                                    <Box sx={dotStyles} />
                                </Box>
                                {["feeSetupHistory"].includes(comingFrom) && (
                                    <>
                                        {["fixed"].includes(`${data?.oldFeeType ?? ""}`.toLowerCase()) && (
                                            <FixedFeeTypeCard label="Previous" feeTypeValue={data?.oldFixedValue ?? ""} />
                                        )}
                                        {["variable"].includes(`${data?.oldFeeType ?? ""}`.toLowerCase()) && (
                                            <VariableFeeTypeCard label="Previous" feeTypeValue={data?.oldVariableRate ?? ""} />
                                        )}
                                        {["slab"].includes(`${data?.oldFeeType ?? ""}`.toLowerCase()) && (
                                            <SlabFeeTypeCard
                                                label="Previous"
                                                slabRows={data?.oldSlabs ?? [{
                                                    slab_min: null,
                                                    slab_max: null,
                                                    rate: null
                                                }]}
                                            />
                                        )}
                                    </>
                                )}
                                {["auditHistory"].includes(comingFrom) && (
                                    <SecurityInfoCard
                                        label="Previous"
                                        isExcluded={data?.oldState?.isExcluded ?? ""}
                                        modifiedAt={data?.oldState?.modifiedAt ?? ""}
                                        modifiedBy={data?.oldState?.modifiedBy ?? ""}
                                        exclusionReason={data?.oldState?.exclusionReason ?? ""}
                                    />
                                )}
                            </Box>
                        </StepLabel>
                    </Step>
                    <Step active>
                        <StepLabel>
                            <Box sx={{ display: "flex", alignItems: "stretch" }}>
                                <Box
                                    sx={{
                                        width: "12px",
                                        flexShrink: 0,
                                        position: "relative",
                                        marginRight: "16px",
                                        "&::before": {
                                            ...lineBase,
                                            top: "-24px",
                                            bottom: "50%",
                                        },
                                    }}
                                >
                                    <Box sx={dotStyles} />
                                </Box>
                                {["feeSetupHistory"].includes(comingFrom) && (
                                    <>
                                        {["fixed"].includes(`${data?.newFeeType ?? ""}`.toLowerCase()) && (
                                            <FixedFeeTypeCard label="New" feeTypeValue={data?.newFixedValue ?? ""} />
                                        )}
                                        {["variable"].includes(`${data?.newFeeType ?? ""}`.toLowerCase()) && (
                                            <VariableFeeTypeCard label="New" feeTypeValue={data?.newVariableRate ?? ""} />
                                        )}
                                        {["slab"].includes(`${data?.newFeeType ?? ""}`.toLowerCase()) && (
                                            <SlabFeeTypeCard
                                                label="New"
                                                slabRows={data?.newSlabs ?? [{
                                                    slab_min: null,
                                                    slab_max: null,
                                                    rate: null
                                                }]}
                                            />
                                        )}
                                    </>
                                )}
                                {["auditHistory"].includes(comingFrom) && (
                                    <SecurityInfoCard
                                        label="New"
                                        isExcluded={data?.newState?.isExcluded ?? ""}
                                        modifiedAt={data?.newState?.modifiedAt ?? ""}
                                        modifiedBy={data?.newState?.modifiedBy ?? ""}
                                        exclusionReason={data?.newState?.exclusionReason ?? ""}
                                    />
                                )}
                            </Box>
                        </StepLabel>
                    </Step>
                </Stepper>
            </Box>
        </Dialog >
    );
}
